// Single source of truth for SDK configuration.
// A game passes a partial config; we merge it over defaults.

export const DEFAULT_CONFIG = {
  gameId: 'untitled-game',     // used to namespace storage + leaderboards
  version: '1.0.0',            // game build version (for save migrations)
  debug: false,               // turn on verbose logging
  logLevel: 'warn',           // silent | error | warn | info | debug

  // Branded bridge / loading screen.
  bridge: {
    enabled: true,
    requireSignIn: true,      // show a Google sign-in button on the splash until signed in
    minDurationMs: 1200,      // keep the splash up at least this long
    brandName: 'The Butterfly Games',
    tagline: 'Press Start',
    // Optional small descriptor under the branded wordmark (only rendered for the
    // "The Butterfly Universe" stacked style, and only when set).
    brandSubtitle: null,      // e.g. 'Media • Entertainment'

    // Progress presentation.
    progressMode: 'bar',      // 'bar' | 'icon-fill'
    showProgressBar: true,    // render the horizontal bar (gates the bar in either mode)
    showProgressText: false,  // render a "NN%" readout under the indicator
    showTagline: true,        // render the tagline line

    // 'icon-fill' mode: the filled icon is clipped vertically (bottom→top) by
    // reportLoadProgress(). Both URLs are required for icon-fill to engage.
    // Defaults point at the SDK-hosted Butterfly brand icons; override per game.
    iconUnfilledUrl: 'https://butterfly-sdk.pages.dev/splash/brand-icon-unfill.png',
    iconFilledUrl: 'https://butterfly-sdk.pages.dev/splash/brand-icon-fill.png',

    // Butterfly Universe brand palette: warm cream stage, deep-navy text, and
    // the orange→gold→magenta wing gradient for accents. Override per game.
    background: '#FAF0DC',    // warm cream
    textColor: '#10243E',     // deep navy (matches the wordmark)
    accentFrom: '#F4571E',    // wing orange
    accentMid: '#F4A81C',     // wing gold
    accentTo: '#DD1E7A',      // wing magenta

    // Optional full-bleed background image (e.g. the icon-less brand artwork).
    // Painted over `background` with cover sizing. Defaults to the SDK-hosted
    // Butterfly artwork; set null to use just the cream color, or a URL per game.
    backgroundImage: 'https://butterfly-sdk.pages.dev/splash/background-image.png',
    logoUrl: null,            // optional <img> src (bar mode mark); falls back to the wing mark
  },

  // Ads: ordered list of providers (a waterfall). First that can serve, wins.
  // Web uses googleH5; Capacitor native uses admobCapacitor (auto-skipped on web).
  // e.g. [
  //   { type: 'admobCapacitor', appId: 'ca-app-pub-XXX~YYY',
  //     interstitialUnitId, rewardedUnitId, bannerUnitId, initializeForTesting: true },
  //   { type: 'googleH5', client: 'ca-pub-XXX',
  //     interstitialSlot: 'gameover', rewardedSlot: 'revive' },
  // ]
  ads: {
    enabled: true,
    providers: [],
    // If every provider declines/errors, rewarded calls resolve as "granted"
    // in dev so game flow never blocks. Set false to deny instead.
    grantRewardOnNoFill: false,
    frequencyHint: '60s',    // interstitial pacing hint for Google H5
  },

  // Shared backend (BaaS). When provider is set, save + leaderboard default to it.
  // One client + one Google player identity is shared across every Butterfly game.
  backend: {
    // Shared Butterfly Supabase project (one identity across all games). The
    // anon key is public/safe to ship; row-level security protects data.
    // A game only needs to set gameId; override these to point elsewhere.
    provider: 'supabase',    // null | 'supabase'
    url: 'https://knenygtrrvdbkheopgaw.supabase.co',
    anonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtuZW55Z3RycnZkYmtoZW9wZ2F3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODE5NzM5MzAsImV4cCI6MjA5NzU0OTkzMH0.1AwZFZRE3p9GSsANPpER8OjNBo0EgDj__L1S3q3XLtQ',
    auth: {
      // Real Google sign-in (redirect flow). No anonymous users: a player has
      // an identity only after they complete Google sign-in.
      provider: 'google',    // 'google' (the only supported sign-in for now)
      redirectTo: null,      // where Google returns the player; null = current URL
      // Single sign-on when embedded in the Butterfly website iframe: the parent
      // forwards the player's Supabase session and the game adopts it. Only
      // messages from these origins are trusted ('*' wildcard for a subdomain).
      trustedParentOrigins: [
        'https://thebutterflyuniverse.com',
        'https://www.thebutterflyuniverse.com',
        'https://the-butterfly-alpha.vercel.app',
        'https://*.pages.dev',
      ],
    },
  },

  // Audio / focus lifecycle. The SDK can't touch your audio directly — it emits
  // 'mute'/'unmute' and 'pause'/'resume' events; your game listens and reacts.
  lifecycle: {
    muteWhileLoading: true,    // mute until start() (game sound only after the splash)
    autoPauseOnHidden: true,   // when the tab is hidden: pause + mute; on return: resume + unmute
    pauseDuringAds: true,      // while an ad is on screen: pause + mute; restore when it closes
  },

  // Analytics. One call — bf.analytics.event('Game started') — routed to the
  // chosen provider. Events are auto-tagged with the platform so you see
  // "<gameId>-web" vs "<gameId>-android" as separate streams.
  analytics: {
    enabled: false,            // opt-in
    provider: 'console',       // 'console' (dev) | 'gameanalytics' | 'none'
    // Local-dev behavior (localhost / 127.0.0.1 / *.local / LAN IP):
    devProvider: null,         // e.g. 'console' — used instead of `provider` in dev
    disableInDev: false,       // true → analytics fully off in dev (overrides devProvider)
    // GameAnalytics keys. Use web keys here; optionally a separate GA game for
    // Android via `android: { gameKey, secretKey }` (auto-selected on device).
    gameKey: null,
    secretKey: null,
    android: null,             // { gameKey, secretKey } — optional Android-specific GA game
    ios: null,                 // { gameKey, secretKey } — optional iOS-specific GA game
    scriptUrl: null,           // override the GameAnalytics HTML5 SDK URL
  },

  // Player identity (shared across all games on this domain).
  identity: {
    defaultName: 'Player',   // display name until the player sets one
  },

  // Save progress.
  storage: {
    adapter: 'auto',         // auto | local | memory | cloud | supabase
    cloud: null,             // { baseUrl, token } when adapter === 'cloud'
  },

  // Leaderboards.
  leaderboard: {
    adapter: 'auto',         // auto | local | remote | supabase
    maxLocalEntries: 50,
    remote: null,            // { baseUrl, token } when adapter === 'remote'
  },
};

function isObject(v) {
  return v && typeof v === 'object' && !Array.isArray(v);
}

export function deepMerge(base, override) {
  const out = Array.isArray(base) ? [...base] : { ...base };
  if (!isObject(override)) return override ?? out;
  for (const key of Object.keys(override)) {
    const b = out[key];
    const o = override[key];
    out[key] = isObject(b) && isObject(o) ? deepMerge(b, o) : o;
  }
  return out;
}

export function resolveConfig(userConfig = {}) {
  const cfg = deepMerge(DEFAULT_CONFIG, userConfig);
  if (cfg.debug && cfg.logLevel === 'warn') cfg.logLevel = 'debug';
  validate(cfg);
  return cfg;
}

function validate(cfg) {
  if (!cfg.gameId || typeof cfg.gameId !== 'string') {
    throw new Error('[ButterflySDK] config.gameId is required and must be a string.');
  }
  if (!Array.isArray(cfg.ads.providers)) {
    throw new Error('[ButterflySDK] config.ads.providers must be an array.');
  }
}
